import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  commitTargetsImport,
  createTarget,
  fetchTargetFacets,
  fetchTargets,
  previewTargetsImport,
  type CsvImportPreview,
  type TargetFilters,
  type TargetListItem,
} from "../../api/targets";
import { addGroupMembers } from "../../api/groups";
import { CsvImportBar } from "../../components/CsvImportBar";
import { Button } from "../../components/ui/Button";

type AddTab = "csv" | "manual" | "existing";

interface AddToGroupProps {
  groupId: string;
  existingIds: Set<string>;
  onChanged: () => void;
}

const TABS: { key: AddTab; label: string }[] = [
  { key: "csv", label: "Import CSV" },
  { key: "manual", label: "Add one" },
  { key: "existing", label: "Add existing" },
];

export function AddToGroup({ groupId, existingIds, onChanged }: AddToGroupProps) {
  const [tab, setTab] = useState<AddTab>("csv");

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Tab strip */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {TABS.map(({ key, label }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
                background: active ? "var(--accent-subtle)" : "transparent",
                color: active ? "var(--accent)" : "var(--text-muted)",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === "csv" && <CsvTab groupId={groupId} onChanged={onChanged} />}
      {tab === "manual" && <ManualTab groupId={groupId} onChanged={onChanged} />}
      {tab === "existing" && (
        <ExistingTab groupId={groupId} existingIds={existingIds} onChanged={onChanged} />
      )}
    </div>
  );
}

// ─── CSV tab ───────────────────────────────────────────────────────────────────────
//
// Reuses the registry CSV import flow (preview → commit). commitTargetsImport
// returns the created targets ({ items, total }); their ids are bulk-added to
// the group. Mirrors the Targets-page import UX (CsvImportBar) but ends in a
// group-add instead of just a registry insert.

function CsvTab({ groupId, onChanged }: { groupId: string; onChanged: () => void }) {
  const [preview, setPreview] = useState<CsvImportPreview | null>(null);
  const [addedCount, setAddedCount] = useState<number | null>(null);

  const previewMut = useMutation({
    mutationFn: (file: File) => previewTargetsImport(file),
    onSuccess: (p) => {
      setPreview(p);
      setAddedCount(null);
    },
  });

  const commitMut = useMutation({
    mutationFn: async (token: string) => {
      const created = await commitTargetsImport(token);
      // /import/commit returns a bare array of { record }, not { items, total }.
      // This read created.items, so the CSV-into-group flow created the targets
      // and then never reached addGroupMembers below — targets imported, group
      // left empty, and the operator saw an error on a partially-applied write.
      const ids = created.map((t) => t.record.target_id);
      if (ids.length > 0) await addGroupMembers(groupId, ids);
      return ids.length;
    },
    onSuccess: (n) => {
      setAddedCount(n);
      setPreview(null);
      onChanged();
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 11,
          color: "var(--text-secondary)",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            border: "1px solid var(--border)",
            background: "var(--bg-tertiary)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          ↑ Choose CSV
        </span>
        <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 10.5 }}>
          name,phone,email,title,org
        </span>
        <input
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) previewMut.mutate(file);
            e.target.value = "";
          }}
        />
      </label>

      {previewMut.error && (
        <div style={{ fontSize: 11, color: "var(--error)", fontFamily: "var(--font-mono)" }}>
          {previewMut.error instanceof Error ? previewMut.error.message : "Failed to read CSV."}
        </div>
      )}

      {preview != null && (
        <CsvImportBar
          totalRows={preview.total_rows}
          validCount={preview.valid.length}
          duplicateCount={preview.duplicates.length}
          invalidCount={preview.invalid.length}
          loading={commitMut.isPending}
          error={commitMut.error instanceof Error ? commitMut.error.message : null}
          onCommit={() => commitMut.mutate(preview.preview_token)}
          onDiscard={() => setPreview(null)}
        />
      )}

      {addedCount != null && (
        <div style={{ fontSize: 11, color: "var(--success)", fontFamily: "var(--font-mono)" }}>
          Added {addedCount} target{addedCount === 1 ? "" : "s"} to this group.
        </div>
      )}
    </div>
  );
}

// ─── Manual tab ──────────────────────────────────────────────────────────────────────
//
// createTarget (registry insert) → addGroupMembers(groupId, [new_id]). Field set
// + the "name required, phone-or-email required" guard match the Targets/Index
// create form so validation parity holds (TargetRef rejects no-contact targets).

function ManualTab({ groupId, onChanged }: { groupId: string; onChanged: () => void }) {
  const [fullName, setFullName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFullName("");
    setFirstName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setRole("");
    setDepartment("");
    setError(null);
  }

  const createMut = useMutation({
    mutationFn: async () => {
      const created = await createTarget({
        full_name: fullName.trim(),
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        primary_phone_e164: phone.trim() || null,
        primary_email: email.trim() || null,
        role: role.trim() || null,
        department: department.trim() || null,
      });
      await addGroupMembers(groupId, [created.target_id]);
    },
    onSuccess: () => {
      reset();
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to add target.");
    },
  });

  const blocked = !fullName.trim() || (!phone.trim() && !email.trim());

  function submit() {
    setError(null);
    if (!fullName.trim()) return;
    if (!phone.trim() && !email.trim()) {
      setError("At least one of phone or email is required.");
      return;
    }
    createMut.mutate();
  }

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
      <Field label="Full name *" value={fullName} onChange={(v) => { setFullName(v); setError(null); }} minWidth={160} />
      <Field label="First name" value={firstName} onChange={setFirstName} minWidth={120} />
      <Field label="Last name" value={lastName} onChange={setLastName} minWidth={120} />
      <Field label="Phone (E.164)" value={phone} onChange={(v) => { setPhone(v); setError(null); }} mono minWidth={150} />
      <Field label="Email" value={email} onChange={(v) => { setEmail(v); setError(null); }} minWidth={160} />
      <Field label="Role" value={role} onChange={setRole} minWidth={120} />
      <Field label="Department" value={department} onChange={setDepartment} minWidth={120} />
      <Button
        variant="primary"
        disabled={blocked || createMut.isPending}
        onClick={submit}
        style={{
          padding: "7px 16px",
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 600,
          border: "1px solid var(--accent)",
          background: blocked ? "var(--bg-tertiary)" : "var(--accent)",
          color: blocked ? "var(--text-muted)" : "white",
          whiteSpace: "nowrap",
        }}
      >
        {createMut.isPending ? "Adding…" : "Add to group"}
      </Button>
      {error && (
        <div style={{ width: "100%", fontSize: 11, color: "var(--error)", fontFamily: "var(--font-mono)" }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Existing tab (facet filter + add-all) ────────────────────────────────────────────
//
// Replaces the old flat checkbox list. Facets (department/role) come from
// fetchTargetFacets; kind→test_only, verified→phone_verified per the contract.
// "Add all N matching" bulk-adds the filtered ids minus current members
// (add_members is idempotent server-side, so re-adds are harmless anyway).

type KindFilter = "any" | "real" | "test";
type TriFilter = "any" | "yes" | "no";

function ExistingTab({
  groupId,
  existingIds,
  onChanged,
}: {
  groupId: string;
  existingIds: Set<string>;
  onChanged: () => void;
}) {
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("");
  const [kind, setKind] = useState<KindFilter>("any");
  const [verified, setVerified] = useState<TriFilter>("any");

  const facetsQuery = useQuery({
    queryKey: ["targets", "facets"],
    queryFn: () => fetchTargetFacets(),
  });

  const filters = useMemo<TargetFilters>(() => {
    const f: TargetFilters = {};
    if (q.trim()) f.q = q.trim();
    if (department) f.department = department;
    if (role) f.role = role;
    if (kind !== "any") f.test_only = kind === "test";
    if (verified !== "any") f.verified = verified === "yes";
    return f;
  }, [q, department, role, kind, verified]);

  const listQuery = useQuery({
    queryKey: ["targets", "list", filters],
    queryFn: () => fetchTargets(filters),
  });

  const candidates = useMemo<TargetListItem[]>(
    () => listQuery.data?.items ?? [],
    [listQuery.data],
  );
  const addableIds = useMemo(
    () => candidates.map((t) => t.target_id).filter((id) => !existingIds.has(id)),
    [candidates, existingIds],
  );

  const addMut = useMutation({
    mutationFn: (target_ids: string[]) => addGroupMembers(groupId, target_ids),
    onSuccess: () => onChanged(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Filter row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search name / phone / email…"
          style={{ ...inputStyle(), flex: 1, minWidth: 200 }}
        />
        <Select
          label="Department"
          value={department}
          onChange={setDepartment}
          options={facetsQuery.data?.departments ?? []}
        />
        <Select
          label="Role"
          value={role}
          onChange={setRole}
          options={facetsQuery.data?.roles ?? []}
        />
        <SelectRaw
          label="Kind"
          value={kind}
          onChange={(v) => setKind(v as KindFilter)}
          options={[
            { value: "any", label: "Any" },
            { value: "real", label: "Real" },
            { value: "test", label: "Test only" },
          ]}
        />
        <SelectRaw
          label="Verified"
          value={verified}
          onChange={(v) => setVerified(v as TriFilter)}
          options={[
            { value: "any", label: "Any" },
            { value: "yes", label: "Verified" },
            { value: "no", label: "Unverified" },
          ]}
        />
      </div>

      {/* Count + add-all */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
          {listQuery.isLoading
            ? "Loading…"
            : `${candidates.length} match${candidates.length === 1 ? "" : "es"} · ${addableIds.length} not yet in group`}
        </span>
        <div style={{ flex: 1 }} />
        <Button
          variant="primary"
          disabled={addableIds.length === 0 || addMut.isPending}
          onClick={() => addMut.mutate(addableIds)}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid var(--accent)",
            background: addableIds.length === 0 ? "var(--bg-tertiary)" : "var(--accent)",
            color: addableIds.length === 0 ? "var(--text-muted)" : "white",
            whiteSpace: "nowrap",
          }}
        >
          {addMut.isPending ? "Adding…" : `Add all ${addableIds.length} matching`}
        </Button>
      </div>

      {addMut.error && (
        <div style={{ fontSize: 11, color: "var(--error)", fontFamily: "var(--font-mono)" }}>
          {addMut.error instanceof Error ? addMut.error.message : "Failed to add members."}
        </div>
      )}

      {/* Result rows */}
      <div
        style={{
          border: "1px solid var(--border-subtle)",
          borderRadius: 8,
          overflow: "auto",
          maxHeight: 300,
        }}
      >
        {listQuery.isLoading ? (
          <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 11, color: "var(--text-muted)" }}>
            Loading…
          </div>
        ) : candidates.length === 0 ? (
          <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 11, color: "var(--text-muted)" }}>
            No matching targets.
          </div>
        ) : (
          candidates.map((t) => {
            const already = existingIds.has(t.target_id);
            const roleLabel = [t.role, t.department].filter(Boolean).join(" · ") || "—";
            return (
              <div
                key={t.target_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1.2fr 1.3fr auto",
                  gap: 10,
                  padding: "8px 14px",
                  borderBottom: "1px solid var(--border-subtle)",
                  alignItems: "center",
                  fontSize: 11,
                }}
              >
                <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{t.full_name}</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)", fontSize: 10.5 }}>
                  {t.primary_phone_e164 ?? "—"}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: 10.5 }}>{roleLabel}</span>
                {already ? (
                  <span style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)", justifySelf: "end" }}>
                    in group
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={addMut.isPending}
                    onClick={() => addMut.mutate([t.target_id])}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      fontSize: 10.5,
                      fontWeight: 600,
                      border: "1px solid var(--accent)",
                      background: "transparent",
                      color: "var(--accent)",
                      whiteSpace: "nowrap",
                      justifySelf: "end",
                    }}
                  >
                    + add
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────────────

function Field({
  label,
  value,
  onChange,
  mono,
  minWidth,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  minWidth: number;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth }}>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle(mono)} />
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 130 }}>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle()}>
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function SelectRaw({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 110 }}>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle()}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function inputStyle(mono = false): React.CSSProperties {
  return {
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "7px 10px",
    color: "var(--text-primary)",
    fontSize: 12,
    fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
    outline: "none",
    boxSizing: "border-box",
  };
}
